'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/common/Header';
import CartItem, { CartItemData } from './CartItem';
import CartSummary from './CartSummary';
import EmptyCart from './EmptyCart';
import Toast, { ToastData } from './Toast';
import {
  getProductsByIds,
  ApiProduct,
  checkWholesalePromoCode,
  getStoredWholesalePromoCode,
  setStoredWholesalePromoCode,
  clearStoredWholesalePromoCode,
  unitPriceForOption,
  PriceMode,
} from '@/lib/api';

interface RawCartItem {
  productId: string;
  quantity: number;
  optionType: 'pack' | 'box';
}

function mergeCartWithProducts(
  rawCart: RawCartItem[],
  products: ApiProduct[],
  priceMode: PriceMode
): CartItemData[] {
  const productMap = new Map(products.map((p) => [p.id, p]));
  const byProduct = new Map<string, { pack: number; box: number }>();
  for (const item of rawCart) {
    if (!byProduct.has(item.productId)) {
      byProduct.set(item.productId, { pack: 0, box: 0 });
    }
    const cur = byProduct.get(item.productId)!;
    if (item.optionType === 'pack') cur.pack += item.quantity;
    else cur.box += item.quantity;
  }
  const result: CartItemData[] = [];
  for (const [productId, qty] of byProduct) {
    if (qty.pack === 0 && qty.box === 0) continue;
    const product = productMap.get(productId);
    if (!product) continue;
    const packOpt = product.purchaseOptions.find((o) => o.type === 'pack');
    const boxOpt = product.purchaseOptions.find((o) => o.type === 'box');
    const packPrice = unitPriceForOption(packOpt, priceMode);
    const boxPrice = unitPriceForOption(boxOpt, priceMode);
    const itemsPerPack = packOpt?.unitsPerOption ?? 1;
    const packsPerBox = boxOpt?.unitsPerOption ?? 1;
    result.push({
      id: productId,
      name: product.name,
      image: product.image || '',
      alt: product.alt || product.name,
      packPrice,
      boxPrice,
      packQuantity: qty.pack,
      boxQuantity: qty.box,
      itemsPerPack,
      packsPerBox,
      minPackQuantity: product.minPackQuantity ?? 1,
      minBoxQuantity: product.minBoxQuantity ?? 1,
      category: product.categoryName || '',
    });
  }
  return result;
}

const ShoppingCartInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [rawCart, setRawCart] = useState<RawCartItem[]>([]);
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [promoDraft, setPromoDraft] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    const stored = getStoredWholesalePromoCode();
    setAppliedPromoCode(stored);
    setPromoDraft(stored);
  }, [isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    const saved = localStorage.getItem('wholesaleCart');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (
          Array.isArray(parsed) &&
          parsed.every(
            (x: unknown) =>
              x && typeof x === 'object' && 'productId' in x && 'quantity' in x && 'optionType' in x
          )
        ) {
          setRawCart(parsed);
        }
      } catch {
        setRawCart([]);
      }
    }
  }, [isHydrated]);

  const cartProductIdsKey = useMemo(
    () =>
      [...new Set(rawCart.map((i) => i.productId))]
        .sort()
        .join(','),
    [rawCart]
  );

  useEffect(() => {
    if (!isHydrated) return;
    const ids = cartProductIdsKey.split(',').filter(Boolean);
    if (ids.length === 0) {
      setProducts([]);
      setProductsLoading(false);
      return;
    }
    setProductsLoading(true);
    getProductsByIds(ids)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, [isHydrated, cartProductIdsKey]);

  const priceMode: PriceMode = appliedPromoCode.trim() ? 'wholesale' : 'retail';

  const cartItems = useMemo(
    () => mergeCartWithProducts(rawCart, products, priceMode),
    [rawCart, products, priceMode]
  );

  const showToast = (message: string, type: ToastData['type']) => {
    setToasts((prev) => [...prev, { id: Date.now().toString(), message, type }]);
  };

  const closeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const persistRawCart = (items: RawCartItem[]) => {
    if (!isHydrated) return;
    localStorage.setItem('wholesaleCart', JSON.stringify(items));
  };

  const handleUpdateQuantity = (id: string, type: 'pack' | 'box', quantity: number) => {
    const next = rawCart.filter((i) => !(i.productId === id && i.optionType === type));
    if (quantity > 0) next.push({ productId: id, quantity, optionType: type });
    setRawCart(next);
    persistRawCart(next);
    showToast('Количество обновлено', 'success');
  };

  const handleRemoveItem = (id: string) => {
    const next = rawCart.filter((i) => i.productId !== id);
    setRawCart(next);
    persistRawCart(next);
    showToast('Товар удален из корзины', 'info');
  };

  const handleClearCart = () => {
    if (window.confirm('Вы уверены, что хотите очистить корзину?')) {
      setRawCart([]);
      persistRawCart([]);
      showToast('Корзина очищена', 'info');
    }
  };

  const handleUpdateQuantities = () => {
    persistRawCart(rawCart);
    showToast('Количество товаров обновлено', 'success');
  };

  const handleApplyPromo = async () => {
    const code = promoDraft.trim();
    if (!code) {
      showToast('Введите промокод', 'warning');
      return;
    }
    setPromoBusy(true);
    try {
      const { valid } = await checkWholesalePromoCode(code);
      if (!valid) {
        showToast('Промокод не найден или неактивен', 'error');
        return;
      }
      setStoredWholesalePromoCode(code);
      setAppliedPromoCode(code);
      showToast('Промокод применён — цены оптовые', 'success');
    } catch {
      showToast('Не удалось проверить промокод', 'error');
    } finally {
      setPromoBusy(false);
    }
  };

  const handleClearPromo = () => {
    clearStoredWholesalePromoCode();
    setAppliedPromoCode('');
    setPromoDraft('');
    showToast('Промокод сброшен — снова розничные цены', 'info');
  };

  const handleProceedToForm = () => {
    const hasInvalidQuantities = cartItems.some(
      (item) =>
        (item.packQuantity > 0 && item.packQuantity < item.minPackQuantity) ||
        (item.boxQuantity > 0 && item.boxQuantity < item.minBoxQuantity)
    );
    if (hasInvalidQuantities) {
      showToast('Проверьте минимальные количества товаров', 'warning');
      return;
    }
    router.push('/order-request-form');
  };

  const totalAmount = cartItems.reduce((sum, item) => {
    return sum + item.packPrice * item.packQuantity + item.boxPrice * item.boxQuantity;
  }, 0);

  const estimatedProcessingDays = cartItems.length > 10 ? 5 : cartItems.length > 5 ? 3 : 2;
  const cartCount = rawCart.reduce((sum, item) => sum + item.quantity, 0);

  if (!isHydrated || productsLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header cartCount={0} />
        <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
          <div className="mb-6">
            <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-48 animate-pulse rounded-lg bg-card" />
                ))}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="h-96 animate-pulse rounded-lg bg-card" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header cartCount={cartCount} />

      <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-text-primary">Корзина заказов</h1>
          {cartItems.length > 0 && (
            <p className="mt-2 font-caption text-base text-text-secondary">
              {cartItems.length}{' '}
              {cartItems.length === 1 ? 'товар' : cartItems.length < 5 ? 'товара' : 'товаров'} в
              корзине
            </p>
          )}
        </div>

        {cartItems.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemove={handleRemoveItem}
                  />
                ))}
              </div>
            </div>

            <div className="lg:col-span-1 space-y-4">
              <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <h2 className="mb-1 font-heading text-base font-semibold text-text-primary">
                  Промокод опта
                </h2>
                <p className="mb-3 font-caption text-xs text-text-secondary">
                  Знаете код — введите его, и корзина пересчитается по оптовым ценам.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={promoDraft}
                    onChange={(e) => setPromoDraft(e.target.value)}
                    placeholder="Промокод"
                    autoComplete="off"
                    className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 font-caption text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoBusy}
                    className="rounded-md bg-primary px-4 py-2 font-heading text-sm font-medium text-primary-foreground transition-all duration-250 hover:bg-primary/90 disabled:opacity-50"
                  >
                    {promoBusy ? 'Проверка…' : 'Применить'}
                  </button>
                </div>
                {appliedPromoCode ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                    <p className="font-caption text-sm text-text-primary">
                      Активен: <span className="font-medium">{appliedPromoCode}</span>
                    </p>
                    <button
                      type="button"
                      onClick={handleClearPromo}
                      className="font-caption text-sm text-accent underline-offset-2 hover:underline"
                    >
                      Сбросить промокод
                    </button>
                  </div>
                ) : null}
              </div>
              <CartSummary
                itemCount={cartItems.length}
                totalAmount={totalAmount}
                estimatedProcessingDays={estimatedProcessingDays}
                onProceedToForm={handleProceedToForm}
                onClearCart={handleClearCart}
                onUpdateQuantities={handleUpdateQuantities}
                wholesalePricing={priceMode === 'wholesale'}
              />
            </div>
          </div>
        )}
      </main>

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-[300] flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onClose={closeToast} />
        ))}
      </div>
    </div>
  );
};

export default ShoppingCartInteractive;
